/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect,  useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-pro";
import { DataGridPro, DataGridProProps } from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import useApi from "../../../hooks/useApi";
import {
  AUDITRACEACTIONS,
  dataGridHeaderHeight,
  dataGridRowHeight,
  GLOBALSETTINGS,
  IMPERSONATION_INFO,
  LOCALSTORAGENAME,
  ModuleNames,
  SEARCHBOX,
  SEVERITIES,
  SYSTEMSETTINGS,
  USERINFO,
} from "../../../data/constants";
import { useSettingValue } from "../../../services/useSettingValue";
import { useUser } from "../../../context/UserContext";
import {
  filterDataBySearchWithGridFields,
  getAccessToken,
  getRefreshToken,
  handleScrollPositionChange,
  setOrRevokeImpersonation,
} from "../../../utils/commonHelper";
import SearchBox from "../../../components/form/SearchBox/SearchBox";
import {
  PortalUserFailedLoginAttemptCountDto,
  PortalUserLisRequest,
  PortalUserListDTO,
} from "../../../types/PortalUser";
import {
  getPortalUserList,
  updatePortalUserFailedLoginAttemptCount,
} from "../../../services/portalUserService";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import { MANAGEUSERSESSION } from "../../../utils/Constants";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";
import {
  Impersonator,
  UserImpersonationDto,
  UserImpersonationRequestDto,
  UserProfileDto,
  UserSessionDto,
} from "../../../types/User";
import {
  getIpAddress,
  getUserProfileInfo,
  impersonateUserDetails,
} from "../../../services/userService";
import { v4 as uuidv4 } from "uuid";
import { useToaster } from "../../../hooks/useToaster";
import {
  GLOBALMESSAGES,
  IMPERSONATE_FAIL,
  IMPERSONATE_SUCCESSFUL,
} from "../../../constants/messages";
import { useDispatch, useSelector } from "react-redux";
import { startImpersonation } from "../../../redux/impersonationSlice";
import { getDeviceType } from "../../../utils/getDeviceType";
import { useNavigate } from "react-router-dom";
import { getCurrentESTDate, getCurrentESTTime } from "../../../utils/dateHelper";
import { useMenu } from "../../../context/MenuContext";
import { RootState } from "../../../redux/store";
import CloseIcon from "@mui/icons-material/Close";
import "./manage.scss";
import CustomLoadingOverlay from "../../../components/ui/CustomLoadingOverlay";
import { logAuditTrace } from "../../../services/auditNavigationTraceService";
import { useResizableColumns } from "../../../hooks/useResizableColumns";
import { useSignalRConnections } from "../../../context/SignalRProvider";

interface PortalUserListProps {
  children?: React.ReactNode;
  isUserDetailEditPermission: boolean;
}

function PortalUserList1(props: PortalUserListProps) {
  const { isUserDetailEditPermission } = props;
  const [searchResult, setSearchResult] = useState("");
  const { loading, executeApi } = useApi();
  const settingValue = useSettingValue(SYSTEMSETTINGS.MANAGEUSERS_PAGINATION);
  const [isPaginationInitialized, setPaginationInitialized] = useState(false);
  const [pageState, setPageState] = useState<{ data: PortalUserListDTO[] | null; total: number }>({
    data: null,
    total: 0,
  });
  const [isClientSearchEvaluated, setIsClientSearchEvaluated] = useState(false);
  const [allData, setAllData] = useState<PortalUserListDTO[]>([]);
  const isFetching = useRef(false);
  const isFirstLoaded = useRef(false);
  const mounted = React.useRef(true);
  const apiRef = useGridApiRef();
  const { userId, userName, firstName, lastName, setUserId, setUserName, setFirstName, setLastName } =
    useUser();
  const [impersonatedUser, setImpersonatedUser] = useState<PortalUserListDTO | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [impersonateReason, setImpersonateReason] = useState("");
  const isImpersonationPendingRef = useRef(false);
  const showToaster = useToaster();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permissions } = useMenu();
  const { isImpersonating } = useSelector((state: RootState) => state.impersonation);
  const [sortModel, setSortModel] = useState<{ field: string; sort: "asc" | "desc" }[]>([]);
  const { restartConnections } = useSignalRConnections();
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10),
  });

  React.useEffect(() => {
    apiRef?.current?.subscribeEvent("scrollPositionChange", handleScrollPositionChange);
    return () => {
      mounted.current = true;
    };
  }, []);

  const handleOnRowsScrollEnd: DataGridProProps["onRowsScrollEnd"] = () => {
    if (loading || !pageState?.data) return;
    if (isFetching.current) return;

    if (pageState?.data && pageState?.data.length >= pageState?.total) {
      return;
    }

    isFetching.current = true;
    setPaginationModel((prev) => {
      const totalPages = Math.ceil(pageState?.total / prev.pageSize);
      if (prev.page + 1 < totalPages) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });
    setTimeout(() => {
      isFetching.current = false;
    }, 100);
  };

  const [state, setState] = useState({
    portalUserList: [] as PortalUserListDTO[],
    portalUser: {} as PortalUserListDTO,
    isInitialLoad: true,
    lockText: "",
    filterText: "",
    page: 0,
    dialogOpen: false,
    dialogTitleText: "",
  });

  const updateState = (newState: Partial<typeof state>) => {
    setState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  const loadData = useCallback(async () => {
    const isNewSearch = paginationModel.page === 0;
    if (isClientSearchEvaluated) {
      // Client-side filtering and sorting
      let filtered = allData;
      if (searchResult) {
        const lowerText = searchResult.toLowerCase();
        filtered = filterDataBySearchWithGridFields(allData, lowerText, searchableFields);
      }

      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        filtered = [...filtered].sort((a, b) => {
          const aValue = a[field];
          const bValue = b[field];
          if (aValue === bValue) return 0;
          if (sort === "asc") return aValue > bValue ? 1 : -1;
          return aValue < bValue ? 1 : -1;
        });
      }

      setPageState({
        data: filtered ?? [],
        total: filtered?.length,
      });

    } else {
      const portalUserLisRequest: PortalUserLisRequest = {
        searchTerm: searchResult,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        sortField: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
      };
      const portalUserListData = await executeApi<PortalUserLisRequest, PortalUserListDTO[]>(
        getPortalUserList,
        portalUserLisRequest
      );

      if (portalUserListData && portalUserListData.length > 0) {
        if (
          !isFirstLoaded.current &&
          portalUserListData[0]?.totalRecords <= paginationModel.pageSize
        ) {
          setIsClientSearchEvaluated(true);
          setAllData(portalUserListData);
        }
        isFirstLoaded.current = true;

        setPageState((prev) => {
          const existingIds = new Set(prev.data?.map((row) => row.rowNumber)); // Track existing IDs
          const newData = portalUserListData.filter((row) => !existingIds.has(row.rowNumber)); // Filter duplicates

          return {
            data: isNewSearch ? portalUserListData : [...(prev.data || []), ...newData],
            total:
              portalUserListData.length > 0 && isNewSearch
                ? portalUserListData[0].totalRecords
                : prev.total,
          };
        });
      } else {
        setPageState({
          data: [],
          total: 0,
        });
      }
    }
    setTimeout(() => {
      apiRef?.current?.scrollToIndexes({ rowIndex: apiRef.current.getScrollPosition().top });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, paginationModel, searchResult, executeApi]);

  useEffect(() => {
    const newPageSize = parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10);

    setPaginationModel((prev) => {
      if (prev.pageSize !== newPageSize) {
        return { page: 0, pageSize: newPageSize };
      }
      return prev;
    });
    setPaginationInitialized(true);
  }, [settingValue]);

  useEffect(() => {
    if (isPaginationInitialized) {
      loadData();
    }
  }, [isPaginationInitialized, loadData]);

  const handleSearchText = useCallback((text: string) => {
    setSearchResult((prev) => (prev !== text ? text : prev));
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));
  }, []);

  const handleEditUserHandler = (user: PortalUserListDTO) => {
    updateState({ lockText: user.isLocked ? MANAGEUSERSESSION.UNLOCK : MANAGEUSERSESSION.LOCK });
    updateState({
      dialogTitleText: user.isLocked
        ? MANAGEUSERSESSION.UNLOCKDIALOGTITLE
        : MANAGEUSERSESSION.LOCKDIALOGTITLE,
    });
    updateState({ portalUser: user });
    openConfirmDialog();
  };

  const handleImpersonateHandler = (user: PortalUserListDTO) => {
    if (isImpersonationPendingRef.current) return;
    setImpersonatedUser(user);
    setOpenDialog(true);
  };

  const openConfirmDialog = () => {
    updateState({ dialogOpen: true });
  };

  const handleDialogClose = async (confirmed: boolean) => {
    if (confirmed) {
      handleEditUser(state.portalUser);
    }
    updateState({ dialogOpen: false });
  };

  const handleEditUser = async (user: PortalUserListDTO) => {
    try {
      user.isLocked = user.isLocked ? false : true;
      // An admin toggling the lock owns the reason for it: locking flags the account as
      // admin-locked so a self-service password reset cannot clear it, unlocking clears the flag
      // and returns the account to the normal failed-attempt behaviour.
      user.isAdminLocked = user.isLocked;
      const response = await executeApi<PortalUserFailedLoginAttemptCountDto, string>(
        updatePortalUserFailedLoginAttemptCount,
        user
      );
      if (response === MANAGEUSERSESSION.SUCCESS) {
        const successMsg = MANAGEUSERSESSION.LOCKUSERSUCCESSMESSAGE.replace(
          "{0}",
          state.portalUser.userName
        ).replace("{1}", state.lockText);

        showToaster(successMsg, SEVERITIES.SUCCESS);
        await loadData();
      }

      let actionDescription = user.isLocked
        ? AUDITRACEACTIONS.LOCKUSER.toString()
        : AUDITRACEACTIONS.UNLOCKUSER.toString();
      if (response !== MANAGEUSERSESSION.SUCCESS) {
        actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
      }

      logAuditTrace(
        {
          userId: userId,
          objectType: "User",
          objectId: user.userId,
          objectName: user.userName,
          action: actionDescription,
          navigationDateTime: getCurrentESTTime(),
        },
        null,
        true
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleConfirm = async () => {
    setOpenDialog(false);
    isImpersonationPendingRef.current = true;
    apiRef.current.forceUpdate();
    const ipAddress = await executeApi<string, string>(getIpAddress, "");
    const newSessionValue = uuidv4();

    const userSessionData: UserSessionDto = {
      IpAddress: ipAddress,
      deviceType: getDeviceType(),
      deviceId: localStorage.getItem("deviceId"),
    };

    const userDetails: UserImpersonationRequestDto = {
      impersonatorUserId: userId,
      impersonatorSessionValue: localStorage.getItem(USERINFO.SESSIONVALUE),
      targetUserId: impersonatedUser.userId,
      impersonatorIpAddress: ipAddress,
      memo: impersonateReason,
      newSessionValue: newSessionValue,
      userSession: userSessionData,
    };

    const impersonatorDetails: Impersonator = {
      impersonatorToken: getAccessToken(),
      impersonatorSessionValue: userDetails.impersonatorSessionValue,
      impersonatorRefreshToken: getRefreshToken(),
      impersonatorUserId: userId,
      impersonatorUserName: userName,
      targetUserName: impersonatedUser.userName,
      impersonatorFirstName: firstName,
      impersonatorLastName: lastName,
      impersonatorPhone: localStorage.getItem(LOCALSTORAGENAME.PHONENUMBER),
      impersonatorEmail: localStorage.getItem(LOCALSTORAGENAME.EMAILADDRESS),
    };

    localStorage.setItem(IMPERSONATION_INFO.Impersonator, JSON.stringify(impersonatorDetails));

    const targetProfile = await executeApi<number, UserProfileDto>(
      getUserProfileInfo,
      impersonatedUser.userId
    );

    const response = await executeApi<UserImpersonationRequestDto, UserImpersonationDto | null>(
      impersonateUserDetails,
      userDetails
    );
    if (response) {
      const currentTime = format(getCurrentESTDate(), GLOBALSETTINGS.DATE_FORMAT_TIME);
      const impersonationMessage = IMPERSONATE_SUCCESSFUL(
        userName,
        impersonatedUser.userName,
        currentTime
      );

      setOrRevokeImpersonation(
        impersonationMessage,
        response.impersonationId,
        response.authToken,
        newSessionValue,
        impersonatedUser.userId,
        impersonatedUser.userName,
        false,
        setUserId,
        setUserName,
        {
          firstName: targetProfile?.cachedFirstName,
          lastName: targetProfile?.cachedLastName,
          phone: targetProfile?.cachedPhoneNumber,
          email: targetProfile?.cachedEmailAddress,
          setFirstName,
          setLastName,
        }
      );
      dispatch(startImpersonation());
      await restartConnections(newSessionValue);
      navigate(GLOBALSETTINGS.DEFAULT_PAGE);
    } else {
      isImpersonationPendingRef.current = false;
      apiRef.current.forceUpdate();
      showToaster(IMPERSONATE_FAIL(impersonatedUser.userName), SEVERITIES.ERROR);
    }
  };

  const columns: GridColDef[] = React.useMemo(() => {
    const userImpersonationPermission = permissions?.find(
      (permission) => permission.moduleName === ModuleNames.UserImpersonation
    );
    const canImpersonate = userImpersonationPermission?.canView ?? false;

    return [
    { field: "userName", headerName: "User Name", minWidth: 100, flex: 1 },
    {
      field: "isOnline",
      headerName: "Is Online",
      sortable: true,
      sortingOrder: ["desc", "asc"],
      width: 100,
      valueGetter: (value, row) => {
        return `${row.isOnline === true}`;
      },
      renderCell: (params) => {
        const isOnline = params.row.isOnline === true;
        if (!isOnline) {
          return "Offline";
        }
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                backgroundColor: isOnline ? "#28a745" : "#6c757d",
                color: "white",
                px: 1,
                py: 0,
                borderRadius: "10px",
                fontSize: "0.7rem",
                fontWeight: 500,
                lineHeight: 1.2,
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ml: "-25px",
                animation: isOnline ? "glow 1s ease-in-out infinite alternate" : "none",
                "@keyframes glow": {
                  from: {
                    boxShadow: "0 0 4px #28a745",
                  },
                  to: {
                    boxShadow: "0 0 10px #28a745",
                  },
                },
              }}
            >
              {"Online"}
            </Box>
          </Box>
        );
      },
    },
    { field: "emailAddress", headerName: "Email Address", minWidth: 200, flex: 1 },
    { field: "phoneNumber", headerName: "Phone Number", minWidth: 200, flex: 1 },
    { field: "roleName", headerName: "Roles", minWidth: 200, flex: 1 },
    {
      field: "lockedOn",
      headerName: "Locked On",
      minWidth: 100,
      flex: 1,
      valueFormatter: (value) => (value ? format(value, GLOBALSETTINGS.DATE_FORMAT) : ""),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.1,
      minWidth: 100,
      hideable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box className="user-actions-cell">
          <IconButton
            size="small"
            disabled={!isUserDetailEditPermission}
            onClick={() => handleEditUserHandler(params.row)}
          >
            {params.row.isLocked ? (
              <Tooltip title={MANAGEUSERSESSION.UNLOCKUSER} placement="top">
                <LockIcon fontSize="small" />
              </Tooltip>
            ) : (
              <Tooltip title={MANAGEUSERSESSION.LOCKUSER} placement="top">
                <LockOpenIcon fontSize="small" />
              </Tooltip>
            )}
          </IconButton>
          {(canImpersonate && !isImpersonating && params.row.userId !== userId) && (
            <IconButton
              size="small"
              className="impersonate-btn"
              disabled={isImpersonationPendingRef.current}
              onClick={() => handleImpersonateHandler(params.row)}
            >
              <Tooltip title="Impersonate" placement="top">
                <Box
                  component="img"
                  src="/Impersonate.gif"
                  alt="impersonate"
                  className="impersonate-icon"
                />
              </Tooltip>
            </IconButton>
          )}
        </Box>
      ),
    },
  ];
  }, [permissions, isImpersonating, isUserDetailEditPermission, userId]);
  const searchableFields = columns.map((col) => col.field).filter((field) => field !== "actions");

  const { columnState, pinnedColumns, setPinnedColumns, handleColumnResize } =
    useResizableColumns(columns);

  return (
    <section className="mb-3">
      <Card variant="outlined">
     <CardContent>
          <Box component="form">
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <SearchBox
                  searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER}
                  onSearch={handleSearchText}
                />
              </Grid2>
              <Grid2 size={12}>
                <div style={{ height: 500, width: "100%" }}>
                  <DataGridPro
                    apiRef={apiRef}
                    rows={pageState?.data ?? []}
                    {...pageState?.data}
                    columns={columnState}
                    rowHeight={dataGridRowHeight}
                    columnHeaderHeight={dataGridHeaderHeight}
                    disableMultipleRowSelection
                    rowCount={pageState?.total}
                    loading={loading}
                    slots={{
                      loadingOverlay: CustomLoadingOverlay,
                    }}
                    paginationMode="server"
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={(model) => {
                      setSortModel(model);
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                    getRowId={(row) => row.rowNumber}
                    hideFooterPagination
                    onRowsScrollEnd={handleOnRowsScrollEnd}
                    localeText={{ noRowsLabel: MANAGEUSERSESSION.NOROWSTEXT }}
                    pinnedColumns={pinnedColumns}
                    onPinnedColumnsChange={(newPinned) => setPinnedColumns(newPinned)}
                    onColumnWidthChange={(params) =>
                      handleColumnResize(params.width, params.colDef)
                    }
                  />
                </div>
              </Grid2>
              <Grid2>
                <ConfirmationDialog
                  open={state.dialogOpen}
                  title={state.dialogTitleText}
                  message={MANAGEUSERSESSION.DIALOGMESSAGE.replace("{0}", state.lockText).replace(
                    "{1}",
                    state.portalUser.userName
                  )}
                  onClose={handleDialogClose}
                />
                <Dialog
                  open={openDialog}
                  onClose={(event, reason) => {
                    if (reason !== GLOBALMESSAGES.BACKDROP) {
                      setOpenDialog(false);
                    }
                  }}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogTitle>Impersonation</DialogTitle>
                  <IconButton
                    aria-label="close"
                    onClick={() => setOpenDialog(false)}
                    sx={() => ({
                      position: "absolute",
                      right: 8,
                      top: 3,
                      color: "#fff",
                    })}
                  >
                    <CloseIcon />
                  </IconButton>
                  <DialogContent dividers>
                    <Typography gutterBottom>
                      Are you sure you want to impersonate{" "}
                      <strong>{impersonatedUser?.userName}</strong>?
                    </Typography>
                    <TextField
                      id="memo"
                      label="Reason for impersonation"
                      multiline
                      sx={{ mt: 1 }}
                      rows={3}
                      fullWidth
                      margin="normal"
                      value={impersonateReason}
                      onChange={(e) => setImpersonateReason(e.target.value)}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} color="secondary">
                      No
                    </Button>
                    <Button onClick={handleConfirm} color="primary" variant="contained">
                      Yes
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid2>
            </Grid2>
          </Box>
        </CardContent>
      </Card>
    </section>
  );
}

export default PortalUserList1;
